import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { type Clip } from "@shared/schema";
import { motion } from "framer-motion";
import { Loader2, Youtube, Trash2, AlertCircle, Clapperboard, Video, ListVideo } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";

const TikTokIcon = (props: React.ComponentProps<'svg'>) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 28 28"
        fill="currentColor"
        {...props}
    >
        <path d="M22.6,8.27a6.45,6.45,0,0,1-5-2.5,1,1,0,0,0-1.25-.37,1,1,0,0,0-.37,1.25,4.41,4.41,0,0,0,3.32,2.82V15.5a5.5,5.5,0,1,1-5.5-5.5V12a1,1,0,1,0,2,0V10a3.5,3.5,0,1,0-3.5,3.5,1,1,0,1,0,2,0,1.5,1.5,0,1,1,1.5-1.5V6.82a6.5,6.5,0,1,0,6.5,6.5,1,1,0,0,0,1-1V9.27A1,1,0,0,0,22.6,8.27Z" />
    </svg>
);

const PlatformIcon = ({ platform }: { platform: 'youtube' | 'tiktok' }) => {
    if (platform === 'tiktok') {
        return <TikTokIcon className="h-4 w-4" />;
    }
    return <Youtube className="h-4 w-4 text-red-500" />;
};

const clipSubmitSchema = z.object({
    videoUrl: z.string().url({ message: "Please enter a valid YouTube or TikTok URL." }),
});

const ClipSubmitForm = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const form = useForm<z.infer<typeof clipSubmitSchema>>({
        resolver: zodResolver(clipSubmitSchema),
        defaultValues: { videoUrl: "" }
    });

    const mutation = useMutation<Clip, Error, z.infer<typeof clipSubmitSchema>>({
        mutationFn: (data) => apiRequest("POST", "/api/clipping/submit", data).then(res => res.json()),
        onSuccess: () => {
            toast({ title: "Clip Submitted!", description: "Your video has been successfully submitted for tracking." });
            queryClient.invalidateQueries({ queryKey: ["my-clips"] });
            form.reset();
        },
        onError: (err: any) => {
            const message = err.response?.data?.message || err.message;
            toast({ title: "Submission Failed", description: message, variant: "destructive" });
        }
    });

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit((values) => mutation.mutate(values))} className="space-y-4">
                <FormField
                    control={form.control}
                    name="videoUrl"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-sm font-medium">Video URL</FormLabel>
                            <FormControl>
                                <div className="relative">
                                    <Clapperboard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                                    <Input placeholder="https://www.youtube.com/watch?v=... or https://www.tiktok.com/.../video/..." {...field} className="pl-10 text-base" />
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <Button type="submit" disabled={mutation.isPending} className="w-full font-semibold text-lg py-6 glow-effect">
                    {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit Clip for Tracking
                </Button>
            </form>
        </Form>
    );
}

const ClipStatusBadge = ({ status, reason }: { status: 'pending' | 'approved' | 'rejected', reason?: string | null }) => {
    const statusMap: { [key in typeof status]: { variant: "default" | "secondary" | "destructive" | "outline", text: string } } = {
        pending: { variant: "secondary", text: "Pending" },
        approved: { variant: "default", text: "Approved" },
        rejected: { variant: "destructive", text: "Rejected" },
    };

    const badge = <Badge variant={statusMap[status].variant}>{statusMap[status].text}</Badge>;

    if (status === 'rejected' && reason) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>{badge}</TooltipTrigger>
                    <TooltipContent>
                        <p className="font-semibold">Rejection Reason</p>
                        <p>{reason}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return badge;
};

const MyClipsManager = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { data: clips, isLoading, error } = useQuery<Clip[], Error>({
        queryKey: ["my-clips"],
        queryFn: () => apiRequest("GET", "/api/clipping/my-clips").then(res => res.json()),
    });

    const deleteMutation = useMutation<{ message: string }, Error, string>({
        mutationFn: (clipId) => apiRequest("DELETE", `/api/clipping/${clipId}`).then(res => res.json()),
        onSuccess: (data) => {
            toast({ title: "Clip Deleted", description: data.message });
            queryClient.invalidateQueries({ queryKey: ["my-clips"] });
        },
        onError: (err: any) => {
            const message = err.response?.data?.message || err.message;
            toast({ title: "Error Deleting Clip", description: message, variant: "destructive" });
        }
    });

    const checkoutMutation = useMutation<
        { message: string; newViews: number; tokensAwarded: number },
        Error,
        string
    >({
        mutationFn: (clipId) =>
            apiRequest("POST", `/api/clipping/${clipId}/checkout`).then((res) =>
                res.json()
            ),
        onSuccess: (data) => {
            toast({
                title: "Checkout Successful!",
                description: data.message,
            });
            queryClient.invalidateQueries({ queryKey: ["my-clips"] });
            queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
        },
        onError: (err: any) => {
            const message = err.response?.data?.message || err.message;
            toast({
                title: "Checkout Failed",
                description: message,
                variant: "destructive",
            });
        },
    });

    if (isLoading) return <div className="text-center p-8">Loading clips...</div>;
    if (error) return <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertTitle>Error</AlertTitle><AlertDescription>{error.message}</AlertDescription></Alert>;

    return (
        <div className="space-y-3">
            {clips && clips.length > 0 ? (
                clips.map((clip, index) => (
                    <motion.div
                        key={clip.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-background/40 p-4 rounded-lg border border-border/50 hover:border-primary/50 transition-all duration-300"
                    >
                        <div className="flex items-center gap-4">
                            <img src={clip.videoThumbnailUrl} alt={clip.videoTitle} className="w-32 h-16 rounded-md object-cover shrink-0 hidden sm:block"/>
                            <div className="flex-grow space-y-2">
                                <a href={clip.videoUrl} target="_blank" rel="noopener noreferrer" className="font-semibold text-base leading-tight line-clamp-2 hover:underline">
                                    {clip.videoTitle}
                                </a>
                                <div className="flex items-center gap-2 flex-wrap">
                                   <PlatformIcon platform={clip.platform} />
                                   <ClipStatusBadge status={clip.status} reason={clip.rejectionReason} />
                                </div>
                                {clip.status === 'approved' && (
                                    <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                                        <span><strong className="text-foreground font-semibold">{clip.lastCheckedViews.toLocaleString()}</strong> Views</span>
                                        <span className="text-primary font-semibold"><strong className="text-primary font-bold">{clip.totalTokensEarned.toLocaleString()}</strong> Tokens Earned</span>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 ml-auto self-start sm:self-center">
                                {clip.status === "approved" && (
                                    <Button
                                        onClick={() => checkoutMutation.mutate(clip.id)}
                                        disabled={checkoutMutation.isPending && checkoutMutation.variables === clip.id}
                                        size="sm"
                                        className="w-full sm:w-auto"
                                    >
                                        {checkoutMutation.isPending && checkoutMutation.variables === clip.id ? (
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        ) : null}
                                        Checkout
                                    </Button>
                                )}
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 h-8 w-8">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This will permanently delete your clip and stop tracking future views. This action cannot be undone.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction onClick={() => deleteMutation.mutate(clip.id)} className="bg-red-500 hover:bg-red-600">
                                                {deleteMutation.isPending ? "Deleting..." : "Delete Clip"}
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>
                    </motion.div>
                ))
            ) : (
                <div className="text-center py-12 border-2 border-dashed border-muted rounded-lg">
                    <Video className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h3 className="mt-4 text-lg font-medium">No Clips Submitted Yet</h3>
                    <p className="mt-1 text-sm text-muted-foreground">Submit a video URL above to start earning tokens from views!</p>
                </div>
            )}
        </div>
    );
}

export default function ClippingPage() {
    return (
        <div className="container mx-auto py-12 px-4 md:px-6 max-w-4xl">
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-10"
            >
                <h1 className="text-4xl font-bold tracking-tight">Clip & Earn</h1>
                <p className="mt-2 text-lg text-muted-foreground">Get paid for your viral videos. Submit a YouTube or TikTok clip and earn tokens for every view it gets.</p>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
            >
                <Card className="glass-effect overflow-hidden">
                    <CardHeader className="bg-primary/5">
                        <CardTitle className="flex items-center gap-2"><Clapperboard /> Submit a New Clip</CardTitle>
                        <CardDescription>Enter the URL of a YouTube or TikTok video you want to track for rewards.</CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                        <ClipSubmitForm />
                    </CardContent>

                    <Separator />

                    <CardHeader className="bg-primary/5">
                        <CardTitle className="flex items-center gap-2"><ListVideo /> My Tracked Clips</CardTitle>
                        <CardDescription>Here are the videos you are currently tracking. Check out their progress and claim your earnings.</CardDescription>
                    </CardHeader>
                    <CardContent className="p-6">
                        <MyClipsManager />
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}